import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/router';
import enTranslations from '@/locales/en.json';
import frTranslations from '@/locales/fr.json';

export type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void | Promise<void>;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: enTranslations,
  fr: frTranslations,
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const router = useRouter();

  // Helper function to get nested object value by string path
  const getNestedValue = (obj: Record<string, unknown>, path: string): string => {
    const result = path.split('.').reduce((current, key) => {
      return current && typeof current === 'object' && key in current
        ? (current as Record<string, unknown>)[key]
        : undefined;
    }, obj as unknown);
    return typeof result === 'string' ? result : path;
  };

  // Translation function
  const t = useCallback((key: string): string => {
    return getNestedValue(translations[language], key);
  }, [language]);

  // Cookie management functions
  const setCookie = (name: string, value: string, days: number = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Set language function that handles routing and cookies
  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    setCookie('language', lang);

    // Pages that don't use /fr prefix (language stored in cookie only)
    const noPrefixPages = ['/start', '/status'];
    const isNoPrefixPage = noPrefixPages.some(page => router.asPath.startsWith(page));

    // For pages that don't use prefix, just update the language state and cookie
    if (isNoPrefixPage) {
      return;
    }

    // Handle URL routing for blog posts with slug translation
    const blogPostMatch = router.asPath.match(/^(\/fr)?\/blog\/([^/?]+)/);

    if (blogPostMatch) {
      const currentSlug = blogPostMatch[2];
      const currentLang = blogPostMatch[1] ? 'fr' : 'en';

      // If switching languages on a blog post, fetch the translated slug
      if (currentLang !== lang) {
        try {
          const response = await fetch(`/api/blog/translate-slug?slug=${currentSlug}&fromLang=${currentLang}&toLang=${lang}`);
          if (response.ok) {
            const data = await response.json();
            const translatedSlug = data.translatedSlug;
            const newPath = lang === 'fr' ? `/fr/blog/${translatedSlug}` : `/blog/${translatedSlug}`;
            router.push(newPath);
            return;
          }
        } catch (error) {
          console.error('Error translating slug:', error);
        }
      }
    }

    // Handle URL routing for other pages
    if (lang === 'fr' && !router.asPath.startsWith('/fr')) {
      // Navigate to French version
      const newPath = router.asPath === '/' ? '/fr' : `/fr${router.asPath}`;
      router.push(newPath);
    } else if (lang === 'en' && router.asPath.startsWith('/fr')) {
      // Navigate to English version (remove /fr prefix)
      const newPath = router.asPath.replace(/^\/fr/, '') || '/';
      router.push(newPath);
    }
  };

  // Initialize language on mount
  useEffect(() => {
    // Pages that don't use /fr prefix (language stored in cookie only)
    const noPrefixPages = ['/start', '/status'];
    const isNoPrefixPage = noPrefixPages.some(page => router.asPath.startsWith(page));

    if (isNoPrefixPage) {
      // For pages without prefix, use cookie or default to English
      const savedLanguage = getCookie('language') as Language;
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'fr')) {
        setLanguageState(savedLanguage);
      } else {
        setLanguageState('en');
        setCookie('language', 'en');
      }
      return;
    }

    // Check URL first for other pages
    if (router.asPath.startsWith('/fr')) {
      setLanguageState('fr');
    } else {
      // Check cookie
      const savedLanguage = getCookie('language') as Language;
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'fr')) {
        if (savedLanguage === 'fr' && !router.asPath.startsWith('/fr')) {
          // Redirect to French version if cookie says French but URL is English
          const newPath = router.asPath === '/' ? '/fr' : `/fr${router.asPath}`;
          router.push(newPath);
        }
        setLanguageState(savedLanguage);
      } else {
        // Default to English
        setLanguageState('en');
        setCookie('language', 'en');
      }
    }
  }, [router.asPath, router]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};